import React, { useCallback } from 'react';
import { getActivityPlugin } from '../../activities/registry';
import { useRoomContext } from '../client/RoomContext';
import type { ActivityAction } from '../types/activity.types';
import type { GameMode } from '../types/gameMode.types';
import { sandboxLogic } from '../../activities/sandbox/sandbox.logic';
import { buildPluginContext } from '../../logic/pluginRunner';

const CONTINUE_MODES: GameMode[] = ['sandbox', 'point', 'poll', 'coop', 'wall'];

export function ExamStudentStage() {
  const { state, submitStudentInput } = useRoomContext();
  const examSlot = state.examSlot;
  const examSession = state.state?.examSession;
  const phase = state.state?.core.phase;

  const dispatch = useCallback(
    (action: ActivityAction) => {
      void submitStudentInput(action.type, action.payload);
    },
    [submitStudentInput],
  );

  if (!examSession || examSession.status !== 'ACTIVE') {
    return (
      <div className="exam-student-idle rounded-xl border border-dashed border-[var(--sala-border)] p-8 text-center">
        <p className="text-sm text-[var(--sala-ink-muted)]">Esperando evaluación del profesor…</p>
      </div>
    );
  }

  if (examSlot?.finished || (examSession.progress[state.participantId ?? '']?.finished)) {
    return (
      <div className="exam-student-done rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-8 text-center">
        <p className="font-display text-2xl font-bold text-emerald-300">¡Evaluación completada!</p>
        <p className="mt-2 text-sm text-[var(--sala-ink-muted)]">
          Has terminado los {examSession.totalBlocks} bloques. Puedes cerrar esta pestaña.
        </p>
      </div>
    );
  }

  if (!examSlot) {
    return (
      <div className="exam-student-loading rounded-xl border border-dashed p-8 text-center">
        <p className="text-sm text-[var(--sala-ink-muted)]">Cargando bloque…</p>
      </div>
    );
  }

  const plugin = getActivityPlugin(examSlot.mode);
  const participant = state.participants.find((p) => p.participantId === state.participantId);
  const team = state.state?.core.routing.teams.find((t) => t.teamId === participant?.teamId);

  let projection: unknown;
  if (examSlot.mode === 'sandbox' && plugin.mode === 'sandbox') {
    try {
      projection = sandboxLogic.getProjection(
        buildPluginContext(
          'sandbox',
          examSlot.payload as never,
          examSlot.runtime as never,
          state.state!.core.routing.mode,
          phase ?? 'ACTIVE',
        ),
      );
    } catch {
      projection = undefined;
    }
  }

  const isStudyGuide = examSession.isStudyGuide === true;
  const showContinue =
    isStudyGuide || CONTINUE_MODES.includes(examSlot.mode);

  return (
    <div className="exam-student-stage flex min-h-0 flex-1 flex-col">
      <div className="exam-student-stage__header mb-3 flex items-center justify-between gap-2 border-b border-[var(--sala-border)] pb-2">
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-bold">{examSession.title}</p>
          {!isStudyGuide && (
            <p className="text-[10px] text-[var(--sala-ink-dim)]">
              {examSlot.blockLabel ?? `Bloque ${examSlot.blockIndex + 1}`}
            </p>
          )}
        </div>
        {!isStudyGuide && (
          <span className="shrink-0 rounded-full bg-[#f5f0ff] px-2.5 py-1 text-[10px] font-bold text-[#6A4C93]">
            {examSlot.blockIndex + 1} / {examSlot.totalBlocks}
          </span>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <plugin.StudentView
          mode={examSlot.mode}
          payload={examSlot.payload as never}
          runtime={examSlot.runtime as never}
          role="student"
          participantId={state.participantId ?? undefined}
          team={team}
          dispatch={dispatch}
          phase={phase}
          projection={projection}
          examAutonomous={examSession.advanceMode === 'AUTONOMOUS'}
        />
      </div>
      {showContinue && phase === 'ACTIVE' && (
        <div className="mt-3 shrink-0 border-t border-[var(--sala-border)] pt-3">
          <button
            type="button"
            className="sala-btn-hero w-full text-xs"
            onClick={() => void submitStudentInput('EXAM_CONTINUE', {})}
          >
            Continuar al siguiente bloque →
          </button>
        </div>
      )}
    </div>
  );
}
